import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable react-hooks/rules-of-hooks */
import { ApolloProvider } from '@apollo/client';
import { NotificationTypes, SmallNotificationBanner } from '@chegg-ui/notification-banner';
import { ThemeProvider, WebfontLoader, createTheme, themes } from '@chegg-ui/theme';
import { Components, Namespace, ProductType, getContent, initializeRioClient, initializeSDK, setCallback, configureApolloForCommerce } from '@chegg/commerce-utils';
import { initClient } from '@chegg/rio2-web-client';
import React, { useEffect, useState } from 'react';
import { DISCONTINUED_PRODUCTS, GENERIC_ERROR_MESSAGE, REVIEW_STEP_COUNTRIES } from '../../constants/appConstants';
import { NOTIFICATION_BANNER_TESTID, ONE_CLICK_TESTID } from '../../constants/dataTestAttributes';
import { PLAN_SELECTION_QUERYNAME } from '../../constants/graphql';
import { PlanSelectionVariations } from '../../constants/interfaces';
import { NewRelicComponentVersion } from '../../constants/newRelic';
import { initApolloClient } from '../../lib/ApolloSchema/apolloUtils';
import { getPlanSelection } from '../../lib/Graphql/getPlanSelection';
import { LoadingIndicator } from '../../modules/LoadingIndicator';
import { getContentfulData } from '../../utils/ContentfulUtil';
import { isCartCheckoutAction, isOrderManagementAction } from '../../utils/FetchActionUtil';
import { sendNewRelicError } from '../../utils/NewRelicUtils';
import { transformAppPropsToOneClickProps } from '../../utils/TransformUtils';
import { getLangPreferenceCookie } from '@chegg/utils';
import CartCheckout from '../CartCheckout';
import OrderManagement from '../OrderManagement';
import { NOTIFICATION_BANNER_ID, USER_REDIRECT, USER_REDIRECT_MESSAGE } from './constants';
import { determineContentToFetch, getEnvironment, shouldRenderOneClick } from './helpers';
import { OneClickContainer } from './styled';
var OneClick = function OneClick(props) {
  var _pricingDetails$data, _pricingDetails$data2, _pricingDetails$data3, _pricingDetails$data4;
  var appPropsAuthorization = props.authorization,
    callback = props.callback,
    _props$loadWebfonts = props.loadWebfonts,
    loadWebfonts = _props$loadWebfonts === void 0 ? true : _props$loadWebfonts,
    optimizelyClient = props.optimizelyClient,
    pricingDetails = props.pricingDetails,
    referrer = props.referrer,
    rioConfig = props.rioConfig,
    couponCode = props.couponCode,
    _props$useAutoApplied = props.useAutoAppliedCoupons,
    useAutoAppliedCoupons = _props$useAutoApplied === void 0 ? true : _props$useAutoApplied,
    _props$sendRioViewEve = props.sendRioViewEvents,
    sendRioViewEvents = _props$sendRioViewEve === void 0 ? true : _props$sendRioViewEve,
    subscriptionId = props.subscriptionId;
  setCallback(callback, Namespace.ONE_CLICK);
  initializeSDK({
    authorization: appPropsAuthorization,
    onError: callback,
    version: NewRelicComponentVersion,
    componentName: Components.ONE_CLICK
  });
  if (!shouldRenderOneClick(pricingDetails)) {
    return /*#__PURE__*/React.createElement("div", {
      "data-test": ONE_CLICK_TESTID
    });
  }

  /* istanbul ignore next */
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    content = _useState2[0],
    setContent = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isRioInitialized = _useState4[0],
    setIsRioInitialized = _useState4[1];
  var client = initApolloClient();
  var newProps = transformAppPropsToOneClickProps(props);
  // Test exists for this, but jest is false-reporting that the line is not covered
  /* istanbul ignore next */
  if (!newProps) return /*#__PURE__*/React.createElement("div", {
    "data-test": ONE_CLICK_TESTID
  });
  var action = newProps.action,
    authorization = newProps.authorization,
    country = newProps.country,
    environment = newProps.environment,
    externalId = newProps.externalId,
    orderDetails = newProps.orderDetails,
    itemData = newProps.itemData,
    isVpnDetected = newProps.isVpnDetected,
    eligibleForCSPUpgrade = newProps.eligibleForCSPUpgrade,
    isMultimonthResubscribe = newProps.isMultimonthResubscribe,
    allowPlanSelection = newProps.allowPlanSelection,
    altPricingData = newProps.altPricingData,
    contextProductType = newProps.contextProductType;
  var isRecurly = itemData[0].catalogItemSource === 'RECURLY';
  var reviewStepEnabled = REVIEW_STEP_COUNTRIES.includes(country) && !isRecurly;
  var orderManagementAction = isOrderManagementAction(action);
  var cartCheckoutAction = isCartCheckoutAction(action);
  var theme = createTheme({}, themes.horizon);
  var uuid = (_pricingDetails$data = pricingDetails.data) === null || _pricingDetails$data === void 0 ? void 0 : _pricingDetails$data.uuid;
  useEffect(function () {
    // initialize Rio 2 client
    initClient();

    // These statements are covered. Jest is being angry with covering this branch case which shouldn't occur
    /* istanbul ignore next */
    if (rioConfig) {
      initializeRioClient(_objectSpread(_objectSpread({}, rioConfig), {}, {
        cheggUuid: uuid
      }), Namespace.ONE_CLICK);
    }

    // TODO PASS DOWN isRioInitialized PROP TO OTHER VIEW TO ENSURE THAT RIO CLIENT IS INITIALIZED BEFORE FIRING THE
    // component_view from CartCheckout/OrderManagement.  Bug: client is not initialized yet, but other child components
    // are already trying to fire the rio event.
    setIsRioInitialized(true);
    var env = getEnvironment(environment);
    var fetchContent = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _determineContentToFe, gql, queryName, contentResponse, _getContentfulData, content;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _determineContentToFe = determineContentToFetch({
                productType: contextProductType || (isMultimonthResubscribe ? ProductType.BUNDLE : itemData[0].productType),
                action: action,
                orderManagementAction: orderManagementAction,
                cartCheckoutAction: cartCheckoutAction,
                country: country
              }), gql = _determineContentToFe.gql, queryName = _determineContentToFe.queryName;
              _context.next = 3;
              return getContent(gql, env);
            case 3:
              contentResponse = _context.sent;
              _getContentfulData = getContentfulData({
                content: contentResponse,
                orderManagementAction: orderManagementAction,
                cartCheckoutAction: cartCheckoutAction,
                externalId: externalId,
                itemData: itemData,
                action: action,
                country: country,
                queryName: queryName
              }), content = _getContentfulData.content;
              setContent(content);
            case 6:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function fetchContent() {
        return _ref.apply(this, arguments);
      };
    }();
    if (DISCONTINUED_PRODUCTS.includes(externalId)) {
      var baseUrl = appPropsAuthorization.cheggBaseUrl;
      var studyUrl = baseUrl ? "".concat(baseUrl, "/study") : '/study';
      sendNewRelicError(USER_REDIRECT, {
        errors: "externalId: ".concat(externalId, ", message: ").concat(USER_REDIRECT_MESSAGE, " ").concat(studyUrl)
      });
      window.location.assign(studyUrl);
    } else if (eligibleForCSPUpgrade && !isMultimonthResubscribe) {
      var _window$location$sear;
      var variation = PlanSelectionVariations.CSP_UPSELL;
      getContent(getPlanSelection({
        variation: variation,
        countryCode: country,
        previewMode: ((_window$location$sear = window.location.search) === null || _window$location$sear === void 0 ? void 0 : _window$location$sear.includes('previewMode=true').toString()) || 'false',
        locale: getLangPreferenceCookie() || 'en'
      }), env).then(function (contentResponse) {
        var _getContentfulData2 = getContentfulData({
            content: contentResponse,
            orderManagementAction: false,
            cartCheckoutAction: false,
            planSelectionVariation: variation,
            externalId: externalId,
            itemData: itemData,
            action: action,
            country: country,
            queryName: PLAN_SELECTION_QUERYNAME
          }),
          content = _getContentfulData2.content;
        setContent(content);
      });
    } else {
      fetchContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var cheggBaseUrl = props.authorization.cheggBaseUrl;
  useEffect(function () {
    var isProd = !(cheggBaseUrl !== null && cheggBaseUrl !== void 0 && cheggBaseUrl.includes('.live.test.cheggnet.com'));
    configureApolloForCommerce(isProd, window.location.hostname);
  }, []);

  /* istanbul ignore next */
  var hasValidOrderManagement = (content === null || content === void 0 ? void 0 : content.orderManagementInfo) && orderManagementAction;
  var hasValidCartCheckout = ((content === null || content === void 0 ? void 0 : content.valueProps) || (content === null || content === void 0 ? void 0 : content.packages)) && cartCheckoutAction;
  var hasError = !content || !orderManagementAction && !cartCheckoutAction;
  return /*#__PURE__*/React.createElement(ApolloProvider, {
    client: client
  }, loadWebfonts && /*#__PURE__*/React.createElement(WebfontLoader, null), /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(OneClickContainer, {
    "data-test": ONE_CLICK_TESTID
  }, /*#__PURE__*/React.createElement(LoadingIndicator, null), hasValidOrderManagement &&
  /*#__PURE__*/
  /* istanbul ignore next */
  React.createElement(OrderManagement, _extends({}, newProps, {
    totalRenewalPriceWithCurrency: orderDetails.totalRenewalPriceWithCurrency,
    content: content,
    token: /* istanbul ignore next */authorization.csrfToken || '',
    isRioInitialized: true,
    isRecurly: isRecurly,
    uuid: /* istanbul ignore next */uuid !== null && uuid !== void 0 ? uuid : '',
    subscriptionId: subscriptionId || ((_pricingDetails$data2 = pricingDetails.data) === null || _pricingDetails$data2 === void 0 || (_pricingDetails$data2 = _pricingDetails$data2.payload) === null || _pricingDetails$data2 === void 0 ? void 0 : _pricingDetails$data2.orderLineId)
  })), hasValidCartCheckout && /*#__PURE__*/React.createElement(CartCheckout, _extends({}, newProps, {
    content: content,
    isVpnDetected: isVpnDetected,
    allowPlanSelection: allowPlanSelection,
    eligibleForCSPUpgrade: eligibleForCSPUpgrade,
    reviewStepEnabled: reviewStepEnabled,
    rioConfig: rioConfig,
    isRioInitialized: isRioInitialized,
    optimizelyClient: optimizelyClient,
    referrer: referrer,
    taxInfo: ((_pricingDetails$data3 = pricingDetails.data) === null || _pricingDetails$data3 === void 0 || (_pricingDetails$data3 = _pricingDetails$data3.payload) === null || _pricingDetails$data3 === void 0 ? void 0 : _pricingDetails$data3.taxInfo) || undefined,
    altTaxInfo: (altPricingData === null || altPricingData === void 0 ? void 0 : altPricingData.taxInfo) || undefined,
    experiment: (_pricingDetails$data4 = pricingDetails.data) === null || _pricingDetails$data4 === void 0 || (_pricingDetails$data4 = _pricingDetails$data4.payload) === null || _pricingDetails$data4 === void 0 || (_pricingDetails$data4 = _pricingDetails$data4.price) === null || _pricingDetails$data4 === void 0 ? void 0 : _pricingDetails$data4.experiment,
    couponCode: couponCode,
    useAutoAppliedCoupons: useAutoAppliedCoupons,
    sendRioViewEvents: sendRioViewEvents
  })), /* istanbul ignore next */hasError && /*#__PURE__*/React.createElement(SmallNotificationBanner, {
    id: NOTIFICATION_BANNER_ID,
    type: NotificationTypes.Error,
    message: GENERIC_ERROR_MESSAGE,
    "data-test": NOTIFICATION_BANNER_TESTID
  }))));
};
export default OneClick;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/one-click/dist/esm/views/OneClick/index.js
// module id = 2886
// module chunks = 11